// src/belediye/components/steps/FinalStatusStep.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

import TrDateTime from "../utils/TrDateTime";
import { safeText, ensureFirstUpperTr, formatPersonNameTr } from "../utils/textFormat";

import { useDispatch, useSelector } from "react-redux";

// FAZ-13: izin cutover'ı (uiOn=false iken eski rol yolu birebir)
import usePermissions from "../../hooks/usePermissions";
import {
  finalizeApplicationStatus,
  fetchApplicationDetails,
  fetchApplicationStatusLogs,
} from "../../redux/slices/belediyeApplicationDetailsSlice";

import CloseApprovalPanel from "./closeApproval/CloseApprovalPanel";
import CitizenDocumentsPanel from "./citizenDelivery/CitizenDocumentsPanel";

import {
  fetchCloseRequestForApplication,
  fetchCloseRequestsHistoryForApplication,
  selectCloseRequestsHistoryForApplication,
  selectCloseRequestsHistoryLoadingForApplication,
  selectCloseRequestsHistoryErrorForApplication,
  fetchLatestApprovedCloseRequestMetaForApplication,
  selectLatestApprovedMetaForApplication,
} from "../../redux/slices/belediyeCloseApprovalSlice";

const LS = {
  user: "user",
  activeEffectiveRole: "activeEffectiveRole",
};

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function resolveSessionRole(user) {
  const eff = localStorage.getItem(LS.activeEffectiveRole);
  if (eff && String(eff).trim()) return String(eff).trim();
  return String(user?.role || "").trim();
}

function isPresident(user) {
  return Boolean(user?.role === "başkan" || user?.is_superuser);
}
function isSupervisorRole(sessionRole) {
  return sessionRole === "personel_1";
}
function isSahaRole(sessionRole) {
  return sessionRole === "saha_personeli";
}
function isUnitStaff(sessionRole, user) {
  if (isPresident(user)) return false;
  if (isSupervisorRole(sessionRole)) return false;
  if (isSahaRole(sessionRole)) return false;
  return true;
}

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));
const isBlank = (s) => !safeStr(s).trim();

const decisionTypeLabel = (decisionType) => {
  const v = safeStr(decisionType).toLowerCase();
  if (v === "approve") return "Onay";
  if (v === "reject") return "Red";
  if (v === "cancel") return "İptal";
  return safeStr(decisionType) || "-";
};

const statusLabel = (s) => {
  if (s === "pending") return "Beklemede";
  if (s === "approved") return "Onaylandı";
  if (s === "rejected") return "Reddedildi";
  if (s === "cancelled") return "İptal Edildi";
  return safeStr(s) || "-";
};

const firstUpperTrSimple = (text) => {
  const s = safeStr(text).trim();
  if (!s) return "";
  const lower = s.toLowerCase();
  if (lower === "çözüldü") return "Çözüldü";
  if (lower === "çözülemedi") return "Çözülemedi";
  try {
    return lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1);
  } catch {
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
};

const isSolvedStatus = (status) => safeStr(status).trim().toLowerCase() === "çözüldü";
const isUnsolvedStatus = (status) => safeStr(status).trim().toLowerCase() === "çözülemedi";

const StatusChipInline = ({ value }) => {
  const raw = safeStr(value).trim();
  if (!raw) return <span className="cap-muted">—</span>;

  const label = firstUpperTrSimple(raw);
  const solved = isSolvedStatus(raw);
  const unsolved = isUnsolvedStatus(raw);

  const cls = solved
    ? "cap-status-chip cap-status-chip--success"
    : unsolved
      ? "cap-status-chip cap-status-chip--danger"
      : "";

  const Icon = solved ? CheckCircleIcon : unsolved ? XCircleIcon : null;
  if (!cls || !Icon) return <span style={{ opacity: 0.85 }}>{label}</span>;

  return (
    <span className={cls}>
      <Icon className="cap-status-chip-icon" />
      {label}
    </span>
  );
};

function pickLatestFinalLog(logs) {
  const filtered = (Array.isArray(logs) ? logs : []).filter(
    (l) => l?.status === "çözüldü" || l?.status === "çözülemedi"
  );
  if (!filtered.length) return null;

  return [...filtered].sort(
    (a, b) => new Date(b.change_date).getTime() - new Date(a.change_date).getTime()
  )[0];
}

// revizyon var mı?
function hasRevision({ proposedSt, proposedNt, finalSt, finalNt }) {
  const pS = safeStr(proposedSt).trim();
  const pN = safeStr(proposedNt).trim();
  const fS = safeStr(finalSt).trim();
  const fN = safeStr(finalNt).trim();

  const statusChanged = Boolean(fS) && fS !== pS;
  const noteChanged = Boolean(fN) && fN !== pN;

  return statusChanged || noteChanged;
}

export default function FinalStatusStep({
  applicationId,
  applicationData,
  panelData,
  updatePanelData,
  onBack,
  isFirstStep,
}) {
  const dispatch = useDispatch();
  const appIdNum = Number(applicationId) || 0;

  // Feature flag (fail-safe: false)
  const closeApprovalEnabled = useSelector((state) =>
    Boolean(state?.belediyeFeatureFlags?.flags?.close_approval)
  );

  // User + sessionRole
  const user = useMemo(() => safeJsonParse(localStorage.getItem(LS.user)), []);
  const sessionRole = useMemo(() => resolveSessionRole(user), [user]);

  // FAZ-13 (3.3): uiOn'da kapatma-onay yetkileri IZINDEN — talep ac:
  // close_request.create; karar ver: close_request.decide. uiOn=false eski yol.
  const { uiOn, can } = usePermissions();
  // FAZ-13 (1): karar verici ÖNCELİKLİ. Render sırası (691 talep paneli → 704
  // karar paneli) create'i önce döndürür; eski yolda userIsUnitStaff ile
  // userIsDecider rolce karşılıklı dışlayıcıydı (isUnitStaff başkan/müdürü ELER).
  // uiOn'da her iki izne sahip kullanıcı (superuser dahil — /me tüm kodları verir)
  // yalnız talep panelini görürdü; decide'ı hariç tutarak dışlayıcılığı geri getir.
  const userIsUnitStaff = uiOn
    ? can("close_request.create") && !can("close_request.decide")
    : isUnitStaff(sessionRole, user);
  const userIsDecider = uiOn
    ? can("close_request.decide")
    : isPresident(user) || isSupervisorRole(sessionRole);

  // Vatandaşa belge teslimi paneli görünürlüğü:
  // birimde özellik AÇIK (applicationData.unit_citizen_document_enabled) + gönderim rolü.
  // KAPALIYSA panel HİÇ mount edilmez → final step bugünküyle bit-for-bit aynı.
  const citizenDocsUnitEnabled = Boolean(applicationData?.unit_citizen_document_enabled);
  // Mount yalnız birim flag'ine bakar; gerçek yetki backend'de (can_view) belirlenir ve
  // panel 403 alırsa kendini gizler. Böylece frontend rol-string tutarsızlığı paneli yanlışlıkla gizlemez.
  const canSeeCitizenDocs = citizenDocsUnitEnabled;

  const renderCitizenDocs = () =>
    canSeeCitizenDocs ? (
      <CitizenDocumentsPanel
        applicationId={applicationId}
        applicationStatus={applicationData?.status}
      />
    ) : null;

  // "Başvuru Sonuçlandırma" başlığı — bilgi metni (i) ikonu arkasına saklı.
  const renderFinalizeHeader = () => (
    <div className="ps-final-header">
      <h3 className="ps-final-title ps-final-title--withinfo">
        Başvuru Sonuçlandırma
        <button
          type="button"
          className="ps-final-info-toggle"
          onClick={() => setInfoOpen((o) => !o)}
          aria-label="Bilgi"
          title="Bilgi"
        >
          <InformationCircleIcon />
        </button>
      </h3>
      {infoOpen && (
        <div className="ps-final-info-box">
          <div className="ps-final-info-icon-main">
            <InformationCircleIcon />
          </div>
          <div className="ps-final-info-text">
            <div>Başvurunun son durumunu seçin ve vatandaşa iletilecek açıklamayı yazın.</div>
            <div>
              <strong>Not alanı zorunludur.</strong> Vatandaş, bu alandaki metni direkt olarak görecektir.
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Pending close request
  const closeReq = useSelector(
    (state) => state?.belediyeCloseApproval?.byApplicationId?.[String(appIdNum)]?.current || null
  );
  const hasPendingCloseReq = safeStr(closeReq?.status) === "pending";

  // Role-based UI switches
  const canUseCloseApprovalRequestUI = closeApprovalEnabled && userIsUnitStaff;
  const canUseCloseApprovalDecideUI = closeApprovalEnabled && userIsDecider;

  // Decider pending ısıt (gelen talep görünmüyor bug fix)
  useEffect(() => {
    if (!closeApprovalEnabled) return;
    if (!userIsDecider) return;
    if (!appIdNum) return;
    dispatch(fetchCloseRequestForApplication({ applicationId: appIdNum }));
  }, [dispatch, closeApprovalEnabled, userIsDecider, appIdNum]);

  /* --------------------------------------------------------------------------
     FINALIZE FORM STATE
  -------------------------------------------------------------------------- */
  const [finalStatus, setFinalStatus] = useState(panelData.finalStatus || "");
  const [finalNote, setFinalNote] = useState(panelData.finalNote || "");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  /* --------------------------------------------------------------------------
     FINAL RESULT STATE (log)
  -------------------------------------------------------------------------- */
  const [success, setSuccess] = useState(false);
  const [lastLog, setLastLog] = useState(null);

  /* --------------------------------------------------------------------------
     HISTORY (LAZY)
  -------------------------------------------------------------------------- */
  const historyItems = useSelector((s) => selectCloseRequestsHistoryForApplication(s, appIdNum));
  const historyLoading = useSelector((s) => selectCloseRequestsHistoryLoadingForApplication(s, appIdNum));
  const historyError = useSelector((s) => selectCloseRequestsHistoryErrorForApplication(s, appIdNum));

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFetchedOnce, setHistoryFetchedOnce] = useState(false);
  const [openHistoryIds, setOpenHistoryIds] = useState(() => new Set());

  // App değişince UI state reset (küçük ama stabil)
  useEffect(() => {
    setHistoryOpen(false);
    setHistoryFetchedOnce(false);
    setOpenHistoryIds(new Set());
    setMetaFetchedOnce(false);
  }, [appIdNum]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleHistoryItem = useCallback((id) => {
    const key = Number(id);
    if (!key) return;
    setOpenHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const ensureHistoryFetched = useCallback(async () => {
    if (!appIdNum) return;
    if (historyFetchedOnce) return;

    setHistoryFetchedOnce(true);
    await dispatch(fetchCloseRequestsHistoryForApplication({ applicationId: appIdNum, maxItems: 25 }))
      .unwrap?.()
      .catch(() => {});
  }, [dispatch, appIdNum, historyFetchedOnce]);

  const handleToggleHistory = useCallback(async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) await ensureHistoryFetched();
  }, [historyOpen, ensureHistoryFetched]);

  /* --------------------------------------------------------------------------
     META (history’den bağımsız): sonuç kartı için onaylayan bilgisi
  -------------------------------------------------------------------------- */
  const latestApprovedMeta = useSelector((s) => selectLatestApprovedMetaForApplication(s, appIdNum));
  const [metaFetchedOnce, setMetaFetchedOnce] = useState(false);

  /* --------------------------------------------------------------------------
     PANEL DATA SYNC
  -------------------------------------------------------------------------- */
  useEffect(() => {
    updatePanelData({ finalStatus, finalNote });
  }, [finalStatus, finalNote, updatePanelData]);

  /* --------------------------------------------------------------------------
     LOG LOAD (only when application is already closed OR after finalize)
  -------------------------------------------------------------------------- */
  const loadLatestLog = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const logs = await dispatch(fetchApplicationStatusLogs({ applicationId, token })).unwrap();
      const latest = pickLatestFinalLog(logs);
      setLastLog(latest);
      setSuccess(Boolean(latest));
    } catch {
      setLastLog(null);
      setSuccess(false);
    }
  }, [dispatch, applicationId]);

  useEffect(() => {
    const st = safeStr(applicationData?.status);
    const alreadyClosed = st === "çözüldü" || st === "çözülemedi";
    if (alreadyClosed) loadLatestLog();
  }, [applicationData?.status, loadLatestLog]);

  // Sonuç kartı meta’sını history’den bağımsız “anlık” çek (1 kere)
  useEffect(() => {
    if (!closeApprovalEnabled) return;
    if (!appIdNum) return;
    if (!success || !lastLog) return;
    if (metaFetchedOnce) return;

    setMetaFetchedOnce(true);
    dispatch(fetchLatestApprovedCloseRequestMetaForApplication({ applicationId: appIdNum }))
      .unwrap?.()
      .catch(() => {});
  }, [dispatch, closeApprovalEnabled, appIdNum, success, lastLog, metaFetchedOnce]);

  /* --------------------------------------------------------------------------
     VALIDATION
  -------------------------------------------------------------------------- */
  const isValid = () => !isBlank(finalStatus) && safeStr(finalNote).trim().length > 0;

  /* --------------------------------------------------------------------------
     FINALIZE ACTION
  -------------------------------------------------------------------------- */
  const handleConfirm = async () => {
    setShowConfirm(false);
    setProcessing(true);
    setError("");

    try {
      const token = localStorage.getItem("accessToken");

      await dispatch(
        finalizeApplicationStatus({
          id: applicationId,
          status: finalStatus,
          note: ensureFirstUpperTr(safeText(finalNote, "")),
          token,
        })
      ).unwrap();

      await dispatch(fetchApplicationDetails({ id: applicationId, token }));
      await loadLatestLog();

      // meta’yı tazelemek için
      setMetaFetchedOnce(false);
    } catch (err) {
      setError(err?.message || "İşlem başarısız oldu. Lütfen tekrar deneyin.");
      setSuccess(false);
    }

    setProcessing(false);
  };

  /* --------------------------------------------------------------------------
     HISTORY SECTION (render)
  -------------------------------------------------------------------------- */
  const renderHistorySection = () => {
    if (!closeApprovalEnabled) return null;

    return (
      <div className="cap-history-wrap">
        <div className="cap-card">
          <div className="cap-history-header">
            <div className="cap-card-title cap-history-title">Talep Geçmişi</div>

            <button
              type="button"
              className="cap-btn cap-btn-secondary cap-btn-inline"
              onClick={handleToggleHistory}
              disabled={historyLoading}
            >
              {historyOpen ? "Kapat" : "Geçmişi Göster"}
              <ChevronDownIcon
                className={["cap-acc-chevron", historyOpen ? "cap-acc-chevron--open" : ""].join(" ")}
              />
            </button>
          </div>

          {historyOpen && (
            <div className="cap-history-body">
              {historyLoading && (
                <div className="cap-alert cap-alert-info cap-mt-10">
                  <ClockIcon className="cap-alert-icon" />
                  <div>Yükleniyor…</div>
                </div>
              )}

              {historyError && (
                <div className="cap-alert cap-alert-error cap-mt-10">
                  <ExclamationTriangleIcon className="cap-alert-icon" />
                  <div>{safeStr(historyError)}</div>
                </div>
              )}

              {!historyLoading && !historyError && Array.isArray(historyItems) && historyItems.length === 0 && (
                <div className="cap-alert cap-alert-info cap-mt-10">
                  <InformationCircleIcon className="cap-alert-icon" />
                  <div>Bu başvuru için geçmiş talep bulunamadı.</div>
                </div>
              )}

              {!historyLoading && !historyError && Array.isArray(historyItems) && historyItems.length > 0 && (
                <div className="cap-history cap-mt-10">
                  {historyItems.map((cr) => {
                    const id = Number(cr?.id || 0);
                    const open = openHistoryIds.has(id);

                    const st = safeStr(cr?.status);
                    const requestedBy2 = safeStr(cr?.requested_by_display || "").trim() || "-";
                    const decidedBy2 = safeStr(cr?.decided_by_display || "").trim() || "-";

                    const proposedSt2 = safeStr(cr?.proposed_status || "").trim();
                    const proposedNt2 = safeStr(cr?.proposed_note || "").trim();
                    const internalNt2 = safeStr(cr?.internal_note || "").trim();

                    const finalSt2 = safeStr(cr?.final_status || "").trim();
                    const finalNt2 = safeStr(cr?.final_note || "").trim();

                    const revised = hasRevision({
                      proposedSt: proposedSt2,
                      proposedNt: proposedNt2,
                      finalSt: finalSt2,
                      finalNt: finalNt2,
                    });

                    return (
                      <div key={id || `cr-${Math.random()}`} className="cap-history-item">
                        <button
                          type="button"
                          className="cap-acc-head"
                          onClick={() => toggleHistoryItem(id)}
                          aria-expanded={open}
                        >
                          <div className="cap-acc-left">
                            <div className="cap-history-action">{statusLabel(st)}</div>
                            <div className="cap-history-date">
                              <CalendarDaysIcon className="cap-mini-icon" />
                              {safeStr(cr?.requested_at || cr?.created_at) ? (
                                <TrDateTime value={cr?.requested_at || cr?.created_at} />
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>

                          <ChevronDownIcon
                            className={["cap-acc-chevron", open ? "cap-acc-chevron--open" : ""].join(" ")}
                          />
                        </button>

                        {open && (
                          <div className="cap-acc-body">
                            <div className="cap-history-meta">
                              <UserIcon className="cap-mini-icon" />
                              Talep Eden: <b>{requestedBy2}</b>
                            </div>

                            <div className="cap-history-msg cap-history-msg--grid">
                              <div className="cap-history-grid">
                                <div className="cap-history-section-title">Personel Önerisi</div>

                                <div className="cap-history-row">
                                  <div className="cap-history-label">Önerilen Statü</div>
                                  <div className="cap-history-value">
                                    <StatusChipInline value={proposedSt2} />
                                  </div>
                                </div>

                                <div className="cap-history-row">
                                  <div className="cap-history-label">Önerilen Vatandaşa Not</div>
                                  <div className="cap-history-value cap-history-value--box">
                                    {proposedNt2 || "—"}
                                  </div>
                                </div>

                                <div className="cap-history-row">
                                  <div className="cap-history-label">Gerekçe</div>
                                  <div className="cap-history-value cap-history-value--box">
                                    {internalNt2 || "—"}
                                  </div>
                                </div>

                                <div className="cap-history-divider" />

                                <div className="cap-history-section-title">Yetkili Kararı</div>

                                <div className="cap-history-row">
                                  <div className="cap-history-label">Karar</div>
                                  <div className="cap-history-value">
                                    {decisionTypeLabel(cr?.decision_type)}
                                  </div>
                                </div>

                                <div className="cap-history-row">
                                  <div className="cap-history-label">Karar Veren</div>
                                  <div className="cap-history-value">{decidedBy2}</div>
                                </div>

                                <div className="cap-history-row">
                                  <div className="cap-history-label">Karar Tarihi</div>
                                  <div className="cap-history-value">
                                    {safeStr(cr?.decided_at) ? <TrDateTime value={cr?.decided_at} /> : "—"}
                                  </div>
                                </div>

                                {safeStr(cr?.decision_note).trim() ? (
                                  <div className="cap-history-row">
                                    <div className="cap-history-label">Karar Notu</div>
                                    <div className="cap-history-value cap-history-value--box">
                                      {safeStr(cr?.decision_note)}
                                    </div>
                                  </div>
                                ) : null}

                                {safeStr(cr?.status) === "approved" ? (
                                  revised ? (
                                    <>
                                      <div className="cap-history-row cap-history-row--revise">
                                        <div className="cap-history-label">Nihai Statü (Revize)</div>
                                        <div className="cap-history-value">
                                          <StatusChipInline value={finalSt2 || proposedSt2} />
                                        </div>
                                      </div>

                                      <div className="cap-history-row cap-history-row--revise">
                                        <div className="cap-history-label">Nihai Vatandaşa Not (Revize)</div>
                                        <div className="cap-history-value cap-history-value--box cap-history-value--revise">
                                          {(finalNt2 || proposedNt2) || "—"}
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="cap-history-row">
                                      <div className="cap-history-label">Revizyon</div>
                                      <div className="cap-history-value cap-history-value--box">
                                        Öneri aynen uygulanmış.
                                      </div>
                                    </div>
                                  )
                                ) : null}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* --------------------------------------------------------------------------
     0) CLOSED VIEW
  -------------------------------------------------------------------------- */
  if (success && lastLog) {
    const solved = safeStr(lastLog?.status) === "çözüldü";
    const showCloseApprovalMeta = Boolean(latestApprovedMeta?.id);

    return (
      <div className="ps-final-root" translate="no">
        <div className={`ps-final-result ${solved ? "ps-final-result--solved" : "ps-final-result--unsolved"}`}>
          <div className="ps-final-result-icon">{solved ? <CheckCircleIcon /> : <XCircleIcon />}</div>

          <h3 className="ps-final-result-title">
            Başvuru <b>{solved ? "ÇÖZÜLDÜ" : "ÇÖZÜLEMEDİ"}</b> olarak sonuçlandırıldı.
          </h3>

          <div className="ps-final-info-list">
            <div className="ps-final-info-row">
              <div className="ps-final-info-icon">
                <UserIcon />
              </div>
              <div className="ps-final-info-content">
                <div className="ps-final-info-label">
                  {showCloseApprovalMeta ? "Sonuçlandıran / Talep Açan" : "Sonuçlandıran"}
                </div>
                <div className="ps-final-info-value">{formatPersonNameTr(lastLog?.changed_by)}</div>
              </div>
            </div>

            {showCloseApprovalMeta && (
              <div className="ps-final-info-row">
                <div className="ps-final-info-icon">
                  <UserIcon />
                </div>
                <div className="ps-final-info-content">
                  <div className="ps-final-info-label">Talebi Onaylayan</div>
                  <div className="ps-final-info-value">
                    {safeStr(latestApprovedMeta?.decided_by_display).trim()
                      ? latestApprovedMeta.decided_by_display
                      : "—"}
                  </div>
                </div>
              </div>
            )}

            <div className="ps-final-info-row">
              <div className="ps-final-info-icon">
                <CalendarDaysIcon />
              </div>
              <div className="ps-final-info-content">
                <div className="ps-final-info-label">Başvuru kapatma tarihi</div>
                <div className="ps-final-info-value">
                  <TrDateTime value={lastLog?.change_date} />
                </div>
              </div>
            </div>

            <div className="ps-final-info-row ps-final-info-row--note">
              <div className="ps-final-info-icon">
                <ChatBubbleLeftRightIcon />
              </div>
              <div className="ps-final-info-content">
                <div className="ps-final-info-label">Vatandaşa iletilen not</div>
                <div className="ps-final-info-value ps-final-info-value--note">
                  {(() => {
                    const raw = safeText(lastLog?.note, "");
                    return raw ? ensureFirstUpperTr(raw) : "—";
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {renderCitizenDocs()}
        {renderHistorySection()}
      </div>
    );
  }

  /* --------------------------------------------------------------------------
     1) Unit staff (feature açık): talep paneli + en altta geçmiş
  -------------------------------------------------------------------------- */
  if (canUseCloseApprovalRequestUI) {
    return (
      <div className="ps-final-root" translate="no">
        {renderCitizenDocs()}
        <CloseApprovalPanel mode="request" applicationId={applicationId} />
        {renderHistorySection()}
      </div>
    );
  }

  /* --------------------------------------------------------------------------
     2) Decider (feature açık): pending varsa decide panel, yoksa klasik finalize
  -------------------------------------------------------------------------- */
  if (canUseCloseApprovalDecideUI) {
    return (
      <div className="ps-final-root" translate="no">
        {hasPendingCloseReq && <CloseApprovalPanel mode="decide" applicationId={applicationId} />}
        {hasPendingCloseReq && renderCitizenDocs()}

        {!hasPendingCloseReq && (
          <>
            {renderFinalizeHeader()}

            {renderCitizenDocs()}
      <div className="ps-final-status-options">
              <button
                type="button"
                className={`ps-final-option ps-final-option--positive ${
                  finalStatus === "çözüldü" ? "ps-final-option--active" : ""
                }`}
                onClick={() => setFinalStatus("çözüldü")}
              >
                <div className="ps-final-option-main">
                  <CheckCircleIcon className="ps-final-option-icon ps-final-option-icon--positive" />
                  <div className="ps-final-option-text">
                    <b>Çözüldü</b>
                    <span>Başvuru başarıyla sonuçlandırılır ve kapatılır.</span>
                  </div>
                </div>
                {finalStatus === "çözüldü" && <span className="ps-final-option-badge">Seçildi</span>}
              </button>

              <button
                type="button"
                className={`ps-final-option ps-final-option--negative ${
                  finalStatus === "çözülemedi" ? "ps-final-option--active" : ""
                }`}
                onClick={() => setFinalStatus("çözülemedi")}
              >
                <div className="ps-final-option-main">
                  <XCircleIcon className="ps-final-option-icon ps-final-option-icon--negative" />
                  <div className="ps-final-option-text">
                    <b>Çözülemedi</b>
                    <span>Teknik, hukuki veya idari sebeplerle başvuru kapatılır.</span>
                  </div>
                </div>
                {finalStatus === "çözülemedi" && <span className="ps-final-option-badge">Seçildi</span>}
              </button>
            </div>

            <div className="ps-final-note-box">
              <label className="ps-final-note-label">
                Vatandaşa gönderilecek not <span className="ps-final-required">*</span>
              </label>
              <textarea
                className={`ps-final-textarea ${
                  touched && !finalNote.trim() ? "ps-final-textarea--error" : ""
                }`}
                rows={3}
                value={finalNote}
                onChange={(e) => setFinalNote(e.target.value)}
                placeholder="Vatandaşa iletilecek açıklamayı buraya yazın..."
              />
            </div>

            {touched && !isValid() && (
              <div className="ps-final-alert ps-final-alert--error">
                <ExclamationTriangleIcon />
                <span>Lütfen bir durum seçin ve vatandaşa gönderilecek notu boş bırakmayın.</span>
              </div>
            )}

            {error && (
              <div className="ps-final-alert ps-final-alert--error">
                <ExclamationTriangleIcon />
                <span>{error}</span>
              </div>
            )}

            <div className="ps-final-actions">
              {!isFirstStep && (
                <button type="button" className="ps-final-button ps-final-button-secondary" onClick={onBack}>
                  Geri
                </button>
              )}

              <button
                type="button"
                className="ps-final-button ps-final-button-primary"
                disabled={!isValid() || processing}
                onClick={() => {
                  setTouched(true);
                  if (isValid()) {
                    setShowConfirm(true);
                    setError("");
                  }
                }}
              >
                Başvuruyu Sonuçlandır
              </button>
            </div>

            {showConfirm && (
              <div className="ps-final-modal-backdrop" onClick={() => !processing && setShowConfirm(false)}>
                <div className="ps-final-modal" onClick={(e) => e.stopPropagation()}>
                  <InformationCircleIcon className="ps-final-modal-icon" />
                  <h3 className="ps-final-modal-title">Onay gerekiyor</h3>
                  <p className="ps-final-modal-text">
                    Başvuruyu sonuçlandırmak istediğinize emin misiniz?
                    <br />
                    Bu işlem geri alınamaz ve vatandaş bu sonucu görecektir.
                  </p>

                  <div className="ps-final-modal-actions">
                    <button
                      type="button"
                      className="ps-final-button ps-final-button-secondary"
                      onClick={() => setShowConfirm(false)}
                      disabled={processing}
                    >
                      Vazgeç
                    </button>

                    <button
                      type="button"
                      className="ps-final-button ps-final-button-primary"
                      onClick={handleConfirm}
                      disabled={processing}
                    >
                      Onayla ve Sonuçlandır
                    </button>
                  </div>
                </div>
              </div>
            )}

            {processing && (
              <div className="ps-final-loading-overlay">
                <div className="ps-final-loader" />
              </div>
            )}
          </>
        )}

        {renderHistorySection()}
      </div>
    );
  }

  /* --------------------------------------------------------------------------
     3) Default: feature kapalı / diğer roller
  -------------------------------------------------------------------------- */
  return (
    <div className="ps-final-root" translate="no">
      {renderFinalizeHeader()}

      {renderCitizenDocs()}
      <div className="ps-final-status-options">
        <button
          type="button"
          className={`ps-final-option ps-final-option--positive ${
            finalStatus === "çözüldü" ? "ps-final-option--active" : ""
          }`}
          onClick={() => setFinalStatus("çözüldü")}
        >
          <div className="ps-final-option-main">
            <CheckCircleIcon className="ps-final-option-icon ps-final-option-icon--positive" />
            <div className="ps-final-option-text">
              <b>Çözüldü</b>
              <span>Başvuru başarıyla sonuçlandırılır ve kapatılır.</span>
            </div>
          </div>
          {finalStatus === "çözüldü" && <span className="ps-final-option-badge">Seçildi</span>}
        </button>

        <button
          type="button"
          className={`ps-final-option ps-final-option--negative ${
            finalStatus === "çözülemedi" ? "ps-final-option--active" : ""
          }`}
          onClick={() => setFinalStatus("çözülemedi")}
        >
          <div className="ps-final-option-main">
            <XCircleIcon className="ps-final-option-icon ps-final-option-icon--negative" />
            <div className="ps-final-option-text">
              <b>Çözülemedi</b>
              <span>Teknik, hukuki veya idari sebeplerle başvuru kapatılır.</span>
            </div>
          </div>
          {finalStatus === "çözülemedi" && <span className="ps-final-option-badge">Seçildi</span>}
        </button>
      </div>

      <div className="ps-final-note-box">
        <label className="ps-final-note-label">
          Vatandaşa gönderilecek not <span className="ps-final-required">*</span>
        </label>
        <textarea
          className={`ps-final-textarea ${touched && !finalNote.trim() ? "ps-final-textarea--error" : ""}`}
          rows={3}
          value={finalNote}
          onChange={(e) => setFinalNote(e.target.value)}
          placeholder="Vatandaşa iletilecek açıklamayı buraya yazın..."
        />
      </div>

      {touched && !isValid() && (
        <div className="ps-final-alert ps-final-alert--error">
          <ExclamationTriangleIcon />
          <span>Lütfen bir durum seçin ve vatandaşa gönderilecek notu boş bırakmayın.</span>
        </div>
      )}

      {error && (
        <div className="ps-final-alert ps-final-alert--error">
          <ExclamationTriangleIcon />
          <span>{error}</span>
        </div>
      )}

      <div className="ps-final-actions">
        {!isFirstStep && (
          <button type="button" className="ps-final-button ps-final-button-secondary" onClick={onBack}>
            Geri
          </button>
        )}

        <button
          type="button"
          className="ps-final-button ps-final-button-primary"
          disabled={!isValid() || processing}
          onClick={() => {
            setTouched(true);
            if (isValid()) {
              setShowConfirm(true);
              setError("");
            }
          }}
        >
          Başvuruyu Sonuçlandır
        </button>
      </div>

      {showConfirm && (
        <div className="ps-final-modal-backdrop" onClick={() => !processing && setShowConfirm(false)}>
          <div className="ps-final-modal" onClick={(e) => e.stopPropagation()}>
            <InformationCircleIcon className="ps-final-modal-icon" />
            <h3 className="ps-final-modal-title">Onay gerekiyor</h3>
            <p className="ps-final-modal-text">
              Başvuruyu sonuçlandırmak istediğinize emin misiniz?
              <br />
              Bu işlem geri alınamaz ve vatandaş bu sonucu görecektir.
            </p>

            <div className="ps-final-modal-actions">
              <button
                type="button"
                className="ps-final-button ps-final-button-secondary"
                onClick={() => setShowConfirm(false)}
                disabled={processing}
              >
                Vazgeç
              </button>

              <button
                type="button"
                className="ps-final-button ps-final-button-primary"
                onClick={handleConfirm}
                disabled={processing}
              >
                Onayla ve Sonuçlandır
              </button>
            </div>
          </div>
        </div>
      )}

      {processing && (
        <div className="ps-final-loading-overlay">
          <div className="ps-final-loader" />
        </div>
      )}
    </div>
  );
}